import React from 'react';
import { useIntl, useSelector } from 'umi';
import Permission from '@/pages/dashboard/components/NoPermission';
import Loading from '@/pages/dashboard/components/Loadings';
import EmbedPreferenceIframe from '@intelligence/components/EmbedPreferenceIframe';
import { getUtilAuthToken } from '@/pages/home/services/authPath';
import { getUserDetail } from '@/pages/home/services/home';
import { getIntelligenceUserGroupNames } from '@intelligence/services/userInfo';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import useAuthPathReport, {
  type IBaseAuthMenu,
  type TAuthPathErrorKey,
} from '@/pages/home/hooks/useAuthPathReport';
import { createAuthPathRpcHandler } from '@home/components/AuthPathReport/rpc';

interface IAuthPathReportMenu extends IBaseAuthMenu {
  id?: string | number;
  u_id?: string | number;
  page_type?: string;
  path?: string;
  auth_path?: string;
  name_en?: string;
}

interface IAuthPathReportProps {
  pageId?: string;
  reportId?: string;
  reportTypeId: string;
  currentMenu?: IAuthPathReportMenu | null;
  resId?: string;
  getAuthPath?: (menu: IAuthPathReportMenu) => string;
  getMenuId?: (menu: IAuthPathReportMenu) => string;
  getPageType?: (menu: IAuthPathReportMenu) => string;
  className?: string;
  style?: React.CSSProperties;
  errorMessages?: Partial<Record<TAuthPathErrorKey, string>>;
}

const DEFAULT_ERROR_MESSAGES: Record<TAuthPathErrorKey, string> = {
  authpath_error_no_report: '未获取到报表信息',
  authpath_error_no_game: '未获取到游戏信息',
  authpath_error_menu_mismatch: '未匹配到菜单',
  authpath_error_no_url: '未获取到报表URL',
  authpath_error_token_empty: '获取鉴权token失败',
  authpath_error_fetch_failed: '获取鉴权信息失败',
};

const AuthPathReport: React.FC<IAuthPathReportProps> = ({
  pageId,
  reportId,
  reportTypeId,
  currentMenu,
  resId = 'intelligence',
  getAuthPath = (menu) => menu.auth_path || menu.path || '',
  getMenuId = (menu) => String(menu.id || menu.u_id || ''),
  getPageType = (menu) => menu.page_type || 'auth_path',
  className,
  style,
  errorMessages,
}) => {
  const { formatMessage } = useIntl();
  const openid = useSelector((state: any) => state?.home?.userInfo?.openid || '');
  const mergedErrors = { ...DEFAULT_ERROR_MESSAGES, ...errorMessages };
  /** 获取用户信息：在原有 getUserDetail 响应基础上追加 group_name（取 intelligence user/info 接口的 groups） */
  const handleGetUserInfo = async () => {
    const [userInfo, groupNames] = await Promise.all([getUserDetail(), getIntelligenceUserGroupNames()]);
    return { ...(userInfo || {}), group_name: groupNames };
  };
  const handleMessage = createAuthPathRpcHandler({
    openid,
    typeId: reportTypeId,
    getUserInfo: handleGetUserInfo,
    reportButtonLog: (btnLogCfg) => {
      buriedBtnPoint({ ...btnLogCfg, pageId: pageId || '' });
    },
  });

  const { loading, errorKey, authUrl } = useAuthPathReport<IAuthPathReportMenu, never>({
    reportId,
    currentMenu,
    currentGame: null,
    fetchToken: getUtilAuthToken,
    getAuthPath,
    getMenuId,
    getPageType,
    buildTokenParams: ({ authPath, menuId, pageType }) => ({
      res_id: resId,
      menu_id: menuId,
      page_type: pageType,
      page_value: authPath,
    }),
  });

  if (loading) return <Loading />;

  if (errorKey) {
    const msg = formatMessage({
      id: errorKey,
      defaultMessage: mergedErrors[errorKey],
    });
    return <Permission type="error" textCN={msg} />;
  }

  return (
    <EmbedPreferenceIframe url={authUrl} className={className} style={style} onMessage={handleMessage} />
  );
};

export default AuthPathReport;
